import React, { useMemo, useState, useRef, useEffect } from 'react';
import { PiCaretLeftLight, PiCaretRightLight, PiCaretDownLight, PiBriefcaseLight } from 'react-icons/pi';
import { FileProps, FolderProps, MatterInfo } from './types';
import { formatDocumentName, formatBytes } from '../../../utils/functions';
import { Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FolderGlyph } from '../fileGlyphs';

/**
 * PURELY A FILE SWITCHER (files-3).
 *
 * It used to repeat the document's name and then line up type, size, created
 * and modified beside it - the name a third time after the breadcrumb and the
 * frame's own file line, and metadata nobody asked for while reading. The name
 * now lives once, in the breadcrumb; the metadata lives behind the header's
 * Info button. What is left here is what the row is actually for: stepping to
 * the next file and opening the folder's contents.
 */
interface FileNavigatorProps {
  files: FileProps[];
  currentFileId: string;
  onFileChange: (fileId: string) => void;
  folders?: FolderProps[];
  onFolderChange?: (folderId: string) => void;
  matterInfo?: MatterInfo | null;
}

const getFileExtension = (fileName: string): string => {
  const ext = fileName.split('.').pop() || '';
  return ext;
};

const FileNavigator: React.FC<FileNavigatorProps> = ({
  files,
  currentFileId,
  onFileChange,
  folders = [],
  onFolderChange,
  matterInfo
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoverTimer, setHoverTimer] = useState<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const currentIndex = useMemo(() => {
    return files.findIndex((f) => f.FileID === currentFileId);
  }, [files, currentFileId]);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < files.length - 1;
  const hasMultiple = files.length > 1 || folders.length > 0;

  const handlePrevious = () => {
    if (hasPrevious) {
      onFileChange(files[currentIndex - 1].FileID);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onFileChange(files[currentIndex + 1].FileID);
    }
  };

  const handleMouseEnter = () => {
    if (!hasMultiple) return;
    const timer = setTimeout(() => {
      setShowDropdown(true);
    }, 350);
    setHoverTimer(timer);
  };

  const handleMouseLeave = () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }
    setShowDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const currentFile = files[currentIndex];

  return (
    <div
      className="glass-bar flex items-center gap-2 px-4 py-1.5 border-b border-hairline relative flex-shrink-0"
      onMouseLeave={handleMouseLeave}
    >
      {/* Previous button */}
      {hasMultiple && (
        <button
          onClick={handlePrevious}
          disabled={!hasPrevious}
          className={`p-1 rounded transition-colors ${
            hasPrevious
              ? 'text-fg-body hover:bg-surface-sunken/70 hover:text-champagne'
              : 'text-fg-muted/50 cursor-not-allowed'
          }`}
          title={t("Previous file")}
        >
          <PiCaretLeftLight className="w-3.5 h-3.5 rtl:-scale-x-100" />
        </button>
      )}

      {/* File switcher with hover trigger */}
      <div
        className={`flex-1 min-w-0 flex items-center gap-2 ${hasMultiple ? 'cursor-pointer' : ''}`}
        onMouseEnter={handleMouseEnter}
        onClick={() => hasMultiple && setShowDropdown(!showDropdown)}
      >
        {currentFile && (
          <>
            <span className="text-sm text-fg-body truncate hover:text-champagne transition-colors">
              {t('Browse this folder')}
            </span>
            {matterInfo ? (
              <Tooltip
                title={
                  <div className="max-w-xs">
                    <div className="font-semibold text-sm mb-1">{matterInfo.title}</div>
                    {matterInfo.description && (
                      <div className="text-xs text-gray-200 mb-1.5 line-clamp-3">{matterInfo.description}</div>
                    )}
                    {matterInfo.clients && matterInfo.clients.length > 0 && (
                      <div className="text-xs text-gray-300 border-t border-gray-500 pt-1 mt-1">
                        {matterInfo.clients.map(c => c.name).filter(Boolean).join(', ')}
                      </div>
                    )}
                    <div className="text-[10px] text-gray-400 mt-1">{t('Click to open matter')}</div>
                  </div>
                }
                arrow
                placement="bottom"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(`/matters/${matterInfo.uuid}`);
                  }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded-full bg-info-50 dark:bg-info-900/30 text-info-700 dark:text-info-300 border border-info-200 dark:border-info-700 hover:bg-info-100 dark:hover:bg-info-800/50 transition-colors cursor-pointer"
                >
                  <PiBriefcaseLight className="w-3 h-3" />
                  <span className="truncate max-w-[140px]">{matterInfo.title}</span>
                </button>
              </Tooltip>
            ) : null}
          </>
        )}
        {/* A file IS open (currentFileId set) but its row hasn't loaded into the
            list yet - show the neutral browse label, never "No file selected"
            over an open document. Only truly show the empty state with no file. */}
        {!currentFile && currentFileId && (
          <span className="text-sm text-fg-body truncate">{t('Browse this folder')}</span>
        )}
        {!currentFile && !currentFileId && (
          <span className="text-sm text-fg-muted">{t('No file selected')}</span>
        )}

        {/* Position in the folder - right side */}
        <div className="flex items-center gap-1.5 ms-auto flex-shrink-0">
          {hasMultiple && (
            <>
              <span className="text-[11px] text-fg-muted ms-1">
                {currentFile ? `${currentIndex + 1} / ${files.length}` : `${files.length} files`}
              </span>
              <PiCaretDownLight className={`w-3 h-3 text-fg-muted transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
            </>
          )}
        </div>
      </div>

      {/* Next button */}
      {hasMultiple && (
        <button
          onClick={handleNext}
          disabled={!hasNext}
          className={`p-1 rounded transition-colors ${
            hasNext
              ? 'text-fg-body hover:bg-surface-sunken/70 hover:text-champagne'
              : 'text-fg-muted/50 cursor-not-allowed'
          }`}
          title={t("Next file")}
        >
          <PiCaretRightLight className="w-3.5 h-3.5 rtl:-scale-x-100" />
        </button>
      )}

      {/* Dropdown menu with smooth animation */}
      <div
        ref={dropdownRef}
        className={`absolute top-full start-3 end-3 mt-0.5 glass rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto custom-scrollbar-main transition-all duration-200 ease-pop origin-top ${
          showDropdown
            ? 'opacity-100 scale-y-100 translate-y-0'
            : 'opacity-0 scale-y-95 -translate-y-1 pointer-events-none'
        }`}
        onMouseEnter={handleMouseEnter}
      >
        {/* Files section */}
        {files.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-[10px] text-fg-muted tracking-wider border-b border-hairline-soft">
              {t('Files')} ({files.length})
            </div>
            <div className="max-h-48 overflow-y-auto custom-scrollbar-main py-1">
              {files.map((file) => {
                const { name } = formatDocumentName(file.FileName);
                const ext = getFileExtension(file.FileName);
                return (
                  <button
                    key={file.FileID}
                    onClick={() => {
                      onFileChange(file.FileID);
                      setShowDropdown(false);
                    }}
                    className={`w-full px-3 py-1.5 text-start text-sm flex items-center gap-2 transition-colors ${
                      file.FileID === currentFileId
                        ? 'bg-surface-soft text-fg-strong border-s-2 border-champagne'
                        : 'text-fg-body hover:bg-surface-sunken/70 border-s-2 border-transparent'
                    }`}
                  >
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] rounded bg-surface-sunken/70 border border-hairline-soft text-fg-muted flex-shrink-0">
                      {ext}
                    </span>
                    <span className="truncate">{name}</span>
                    {file.FileSize != null && file.FileSize > 0 && (
                      <span className="text-[10px] text-fg-muted ms-auto flex-shrink-0">
                        {formatBytes(file.FileSize)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Folders section */}
        {folders.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-[10px] text-fg-muted tracking-wider border-t border-hairline-soft">
              {t('Folders')} ({folders.length})
            </div>
            <div className="max-h-48 overflow-y-auto custom-scrollbar-main py-1">
              {folders.map((folder) => (
                <button
                  key={folder.FolderID}
                  onClick={() => {
                    if (onFolderChange) {
                      onFolderChange(folder.FolderID);
                    }
                    setShowDropdown(false);
                  }}
                  className="w-full px-3 py-1.5 text-start text-sm text-fg-body hover:bg-surface-sunken/70 transition-colors flex items-center gap-2"
                >
                  <FolderGlyph folderName={folder.FolderName} className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{folder.FolderName}</span>
                  {folder.FileCount > 0 && (
                    <span className="text-[10px] text-fg-muted ms-auto flex-shrink-0">
                      {folder.FileCount} {t('files')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileNavigator;
